import { makeStyles, useMediaQuery, useTheme, Theme } from '@material-ui/core'
import AddIcon from '@mui/icons-material/Add'
import DoneIcon from '@mui/icons-material/Done'
import NotInterestedIcon from '@mui/icons-material/NotInterested'
import React, { FC, useEffect, useState } from 'react'
import {
  DELETE,
  GET_LIST,
  required,
  sanitizeFetchType,
  SaveButton,
  SimpleForm,
  Toolbar,
  UPDATE,
  useMutation,
  useQueryWithStore,
  useRefresh,
  useShowContext,
  useTranslate,
} from 'react-admin'
import { v4 as uuidv4 } from 'uuid'
import { ResourceName } from '../../core/ResourceName'
import { useHasAuthority } from '../hooks/useHasAuthority'
import { Authority } from '../../core/auth/Authority'
import Button from './customized-mui-components/Button'
import { ConfirmDialog } from './ConfirmDialog'
import FilteredReferenceInput from './FilteredReferenceInput'
import LinkedPolygon from './LinkedPolygon'
import {
  DeviceManagementLinkDraftPoint,
  DeviceManagementLinkDto,
} from '../../core/dto/device/device-management/device-management-links/DeviceManagementLink.dto'
import { CreateDialog } from './customized-ra-components/CreateDialog'

const useStyles = makeStyles((theme: Theme) => ({
  hide: {
    display: 'none',
  },
  bulkStateActions: {
    height: '16px',
    background: 'rgba(0,0,0,0.2)',
    minWidth: '300px',
    userSelect: 'none',
  },
  bulkProgressTitle: {
    paddingLeft: '10px',
    width: '100%',
  },
  bulkProgressCloseButton: {
    margin: 0,
  },
  actionsButton: {
    margin: '2%',
    width: '96%',
  },
  dialogTitle: {
    paddingLeft: '10px',
    width: '100%',
  },
  linkToolbar: {
    position: 'absolute',
    top: 8,
    left: 8,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.background.default}`,
    borderRadius: 6,
    padding: '4px 8px',
    display: 'flex',
    gap: 8,
    pointerEvents: 'auto',
    zIndex: 101,
    minHeight: '32px',
  },
  overlaySVG: {
    position: 'absolute',
    top: 0,
    left: 0,
    pointerEvents: 'auto',
  },
}))

type Props = {
  recordId: number
  resource: ResourceName
  containerRef: React.RefObject<HTMLDivElement>
  containerWidth: number
  containerHeight: number
  editMode: boolean
}

const ImageLinksOverlay: FC<Props> = ({
  recordId,
  resource,
  containerRef,
  containerWidth,
  containerHeight,
  editMode,
}) => {
  const { basePath } = useShowContext()
  const classes = useStyles()
  const translate = useTranslate()
  const theme = useTheme()
  const refresh = useRefresh()
  const [mutate] = useMutation()
  const hasAuthority = useHasAuthority()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [areas, setAreas] = useState<DeviceManagementLinkDto[]>([])
  const [draft, setDraft] = useState<DeviceManagementLinkDraftPoint[]>([])
  const [isDrawing, setIsDrawing] = useState(false)
  const [finishedDraft, setFinishedDraft] = useState(false)
  const [dragging, setDragging] = useState<{
    areaId: number
    pointId: string
  } | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [linkToDelete, setLinkToDelete] = useState<number | undefined>(
    undefined,
  )
  const [targetIdDialogOpen, setTargetIdDialogOpen] = useState(false)

  const containerScale = 10000

  const { data: deviceManagementLinks } = useQueryWithStore({
    type: sanitizeFetchType(GET_LIST),
    resource: ResourceName.DEVICE_MANAGEMENT_LINKS,
    payload: {
      filter: {
        containerId: recordId,
      },
    },
  })

  // Load links from Database
  useEffect(() => {
    if (deviceManagementLinks) setAreas(deviceManagementLinks)
  }, [deviceManagementLinks])

  const handleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!isDrawing || finishedDraft || !containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    const xPosition = e.clientX - rect.left
    const yPosition = e.clientY - rect.top

    const xScaled = Math.round((xPosition / rect.width) * containerScale)
    const yScaled = Math.round((yPosition / rect.height) * containerScale)

    setDraft((prev) => [
      ...prev,
      { id: uuidv4(), xPosition: xScaled, yPosition: yScaled },
    ])
  }

  const handleStart = () => {
    setDraft([])
    setIsDrawing(true)
    setFinishedDraft(false)
  }

  const handleFinish = () => {
    if (draft.length < 3) return
    setTargetIdDialogOpen(true)
  }

  const handleUpdateDeviceManagementLink = (
    movedArea: DeviceManagementLinkDto,
  ) => {
    mutate({
      resource: ResourceName.DEVICE_MANAGEMENT_LINKS,
      type: sanitizeFetchType(UPDATE),
      payload: {
        data: movedArea,
      },
    })
  }

  const handleCancel = () => {
    setTargetIdDialogOpen(false)
    setDraft([])
    setIsDrawing(false)
    setFinishedDraft(false)
  }

  const handleDeleteLink = async (areaId: number) => {
    setLinkToDelete(areaId)
    setConfirmOpen(true)
  }

  const confirmDeleteLink = () => {
    mutate({
      resource: ResourceName.DEVICE_MANAGEMENT_LINKS,
      type: sanitizeFetchType(DELETE),
      payload: {
        id: linkToDelete,
      },
    })
    setLinkToDelete(undefined)
    setConfirmOpen(false)
    refresh()
  }

  const toPixels = (p: DeviceManagementLinkDraftPoint) => ({
    xPosition: (p.xPosition / containerScale) * containerWidth,
    yPosition: (p.yPosition / containerScale) * containerHeight,
  })

  /* --- Editing logic --- */
  const handleMouseDown = (
    e: React.MouseEvent<SVGCircleElement, MouseEvent>,
    areaId: number,
    pointId: string,
  ) => {
    if (!editMode) return
    e.stopPropagation()
    setDragging({ areaId, pointId })
  }

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    if (!dragging || !containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    const xPosition = e.clientX - rect.left
    const yPosition = e.clientY - rect.top
    const xScaled = Math.round((xPosition / rect.width) * containerScale)
    const yScaled = Math.round((yPosition / rect.height) * containerScale)

    setAreas((prev) =>
      prev.map((a) =>
        a.id === dragging.areaId
          ? {
              ...a,
              coordinates: a.coordinates.map((pt) =>
                pt.id === dragging.pointId
                  ? { ...pt, xPosition: xScaled, yPosition: yScaled }
                  : pt,
              ),
            }
          : a,
      ),
    )
  }

  const handleMouseUp = () => {
    if (dragging) {
      const movedArea = areas.find((a) => a.id === dragging.areaId)
      if (movedArea) {
        handleUpdateDeviceManagementLink(movedArea)
        setDragging(null)
      }
    }
  }

  useEffect(() => {
    if (!editMode && isDrawing) handleCancel()
  }, [editMode, isDrawing])

  const makeUrlFromId = (targetId: number) => `/#${basePath}/${targetId}/show`

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
        className={
          editMode && hasAuthority(Authority.CREATE_DEVICE_MANAGEMENT_LINKS)
            ? classes.linkToolbar
            : classes.hide
        }
      >
        {/* (hidden when collapsed) */}
        <ConfirmDialog
          open={confirmOpen}
          cancel={() => setConfirmOpen(false)}
          confirm={() => confirmDeleteLink()}
          source="removeLink"
          action="remove"
          resource={ResourceName.DEVICE_MANAGEMENT_LINKS}
        />

        <CreateDialog
          basePath={`/${ResourceName.DEVICE_MANAGEMENT_LINKS}`}
          resource={ResourceName.DEVICE_MANAGEMENT_LINKS}
          title={translate(
            `resources.${ResourceName.DEVICE_MANAGEMENT_LINKS}.titles.create`,
          )}
          handleClose={handleCancel}
          open={targetIdDialogOpen}
          disableBackdropClick
          disableEscapeKeyDown
        >
          <SimpleForm
            initialValues={{
              containerId: recordId,
              coordinates: draft,
            }}
            redirect={false}
            toolbar={
              <Toolbar>
                <SaveButton redirect={false} onClick={handleCancel} />
              </Toolbar>
            }
          >
            <FilteredReferenceInput
              source="targetContainerId"
              reference={resource}
              sort={{ field: 'id', order: 'ASC' }}
              perPage={smallScreen ? 5 : 15}
              filterSource="search"
              selectWithPaginationInputProps={{
                optionText: 'name',
                showFilter: true,
              }}
              filter={{
                excludeId: recordId,
              }}
              label={`resources.${resource}.fields.name`}
              validate={required()}
            />
          </SimpleForm>
        </CreateDialog>
        {hasAuthority(Authority.CREATE_DEVICE_MANAGEMENT_LINKS) && (
          <div className={!editMode && !isDrawing ? classes.hide : ''}>
            <Button
              color="primary"
              onClick={handleStart}
              label={
                editMode
                  ? translate(
                      `resources.${ResourceName.DEVICE_MANAGEMENT_LINKS}.actions.newLink`,
                    )
                  : ''
              }
              disabled={isDrawing}
              className={isDrawing ? classes.hide : ''}
            >
              <AddIcon />
            </Button>
            <Button
              color="primary"
              onClick={handleFinish}
              label={
                editMode
                  ? translate(
                      `resources.${ResourceName.DEVICE_MANAGEMENT_LINKS}.actions.finishLink`,
                    )
                  : ''
              }
              disabled={!isDrawing || draft.length < 3}
              className={!isDrawing || draft.length < 3 ? classes.hide : ''}
            >
              <DoneIcon />
            </Button>
            <Button
              color="primary"
              onClick={handleCancel}
              label={editMode ? translate('const.cancel') : ''}
              disabled={!isDrawing}
              className={!isDrawing ? classes.hide : ''}
            >
              <NotInterestedIcon fontSize="small" />
            </Button>
          </div>
        )}
      </div>

      {/* SVG overlay */}
      {hasAuthority(Authority.VIEW_DEVICE_MANAGEMENT_LINKS) && (
        <svg
          id="linksOverlaySVG"
          width={containerWidth}
          height={containerHeight}
          className={classes.overlaySVG}
          style={{
            cursor: isDrawing ? 'copy' : 'default',
          }}
          onClick={handleClick}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          {/* Saved links */}
          {areas.map((area) => {
            const points = area.coordinates
              .map((p) => {
                const { xPosition, yPosition } = toPixels(p)
                return `${xPosition},${yPosition}`
              })
              .join(' ')

            // centroid for delete button
            const centroid = area.coordinates.reduce(
              (acc, p) => {
                const { xPosition, yPosition } = toPixels(p)
                return {
                  xPosition: acc.xPosition + xPosition,
                  yPosition: acc.yPosition + yPosition,
                }
              },
              { xPosition: 0, yPosition: 0 },
            )
            centroid.xPosition /= area.coordinates.length
            centroid.yPosition /= area.coordinates.length

            return (
              <g key={area.id}>
                {editMode ? (
                  <polygon
                    points={points}
                    fill={theme.palette.primary.main}
                    fillOpacity={0.3}
                    stroke="blue"
                    strokeWidth={2}
                  />
                ) : (
                  <LinkedPolygon
                    points={points}
                    href={makeUrlFromId(area.targetContainerId)}
                  />
                )}

                {/* Edit mode controls */}
                {editMode && (
                  <>
                    {/* Drag handles */}
                    {area.coordinates.map((pt) => {
                      const { xPosition, yPosition } = toPixels(pt)
                      return (
                        <circle
                          key={pt.id}
                          cx={xPosition}
                          cy={yPosition}
                          r={5}
                          fill="white"
                          stroke="blue"
                          strokeWidth={2}
                          style={{ cursor: 'move' }}
                          onMouseDown={(e) =>
                            handleMouseDown(e, area.id, pt.id)
                          }
                        />
                      )
                    })}
                    {/* Delete button */}
                    {hasAuthority(Authority.DELETE_DEVICE_MANAGEMENT_LINKS) && (
                      <g
                        onClick={() => handleDeleteLink(area.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <circle
                          cx={centroid.xPosition}
                          cy={centroid.yPosition}
                          r={8}
                          fill="red"
                          stroke="white"
                          strokeWidth={2}
                        />
                        <text
                          x={centroid.xPosition}
                          y={centroid.yPosition + 3}
                          textAnchor="middle"
                          fontSize="10"
                          fill="white"
                          pointerEvents="none"
                        >
                          X
                        </text>
                      </g>
                    )}
                  </>
                )}
              </g>
            )
          })}

          {/* Draft link preview */}
          {draft.length >= 2 &&
            (draft.length >= 3 ? (
              <polygon
                points={draft
                  .map((p) => {
                    const { xPosition, yPosition } = toPixels(p)
                    return `${xPosition},${yPosition}`
                  })
                  .join(' ')}
                fill="rgba(255,0,0,0.2)"
                stroke="red"
                strokeDasharray={finishedDraft ? '0' : '4'}
              />
            ) : (
              <polyline
                points={draft
                  .map((p) => {
                    const { xPosition, yPosition } = toPixels(p)
                    return `${xPosition},${yPosition}`
                  })
                  .join(' ')}
                fill="none"
                stroke="red"
                strokeDasharray="4"
              />
            ))}

          {/* Draft points */}
          {draft.map((pt, idx) => {
            const { xPosition, yPosition } = toPixels(pt)
            return (
              <circle
                key={pt.id}
                cx={xPosition}
                cy={yPosition}
                r={idx === 0 ? 5 : 4}
                fill={idx === 0 ? 'white' : 'red'}
                stroke="red"
                strokeWidth={idx === 0 ? 2 : 1}
              />
            )
          })}
        </svg>
      )}
    </div>
  )
}

export default ImageLinksOverlay
